
import { ReactNode, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/subscription";
import { toast } from "sonner";
import LoadingScreen from "@/components/ui/LoadingScreen";

interface ProtectedRouteProps {
  children: ReactNode;
  requiresSubscription?: boolean;
}

/**
 * Component that protects routes requiring subscription
 */
const ProtectedRoute = ({ 
  children, 
  requiresSubscription = false 
}: ProtectedRouteProps) => {
  const { isSubscribed, isLoading, status } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoading) {
      return; // Wait for subscription check to complete
    }

    // If API returned an error, we'll default to allowing access and log it
    if (status.error) {
      console.warn("Subscription check error, allowing access:", status.error);
      return;
    }

    if (requiresSubscription && !isSubscribed) {
      // If we're trying to access a subscription-required route without a subscription
      console.log("Redirecting to pricing: User attempted to access protected route without subscription");
      
      // Add a toast to explain the redirect
      toast.info("This feature requires a subscription. Starting your 7-day free trial is easy!");
      
      // Redirect to pricing page with the return URL as query param
      const returnPath = encodeURIComponent(location.pathname + location.search);
      navigate(`/pricing?returnTo=${returnPath}`);
    }
  }, [isSubscribed, isLoading, navigate, requiresSubscription, location, status.error]);

  // Show proper loading screen while checking subscription status
  if (isLoading && requiresSubscription) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
